'use client';

import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { type GenericExposeListing, type MasterDataShopResponse, type SearchListingsFilters } from '@pkgs/api';
import { type FooterAccordionProps } from '@ev-tech/ui-core-components/FooterAccordion';
import { type ContactsProps, Footer, type StoryblokFooterProps } from '@ev-tech/ui-core-components/Footer';
import { type SbBaseBlockProps, type StoryBlokLinkProps, type StoryblokDatasourceEntry } from '@ev-tech/ui-core-components/cms';
import { UserActionEnum } from '@ev-tech/snowplow-library';

import { type NavigationTrackingContext } from '@/types/tracking';
import { useDidomi } from '@/providers/DidomiContextProvider';
import { useSettings } from '@/hooks/useSettings';
import { usePageTrackingTypes } from '@/hooks/usePageTrackingTypes';

import { getLinkUrl, linkResolver } from '../utils/linkResolver';
import { formatPhoneLink } from '../utils/formatPhoneLink';
import { trackNavigationCustomEvent, trackUserInteraction } from '../lib/tracking/snowplow';
import { ELEMENT_IDS } from '../constants';

type FooterBlockProps = SbBaseBlockProps<StoryblokFooterProps> & {
  externalLinkDomains?: StoryblokDatasourceEntry[] | null;
  filters?: SearchListingsFilters;
  pageTrackingTypes?: PageTrackingTypes;
  listing?: GenericExposeListing;
  isMapToggledOn?: boolean;
  searchMode?: SearchMode;
  shop?: MasterDataShopResponse;
};

export const FooterBlock = ({
  blok,
  externalLinkDomains,
  filters,
  pageTrackingTypes: providedPageTrackingTypes,
  listing,
  isMapToggledOn,
  searchMode,
  shop,
}: FooterBlockProps) => {
  const { countryCode, language, measurementSystem } = useSettings();
  const didomi = useDidomi();
  const inferredPageTrackingTypes = usePageTrackingTypes();
  const pageTrackingTypes = providedPageTrackingTypes ?? inferredPageTrackingTypes;

  const trackLegalLink = (type: string, link: StoryBlokLinkProps) => {
    trackItemNavigation({
      elementId: `${ELEMENT_IDS.metaFooter.title}_${type}`,
      href: getLinkUrl(link),
    });
  };

  const trackPhoneClick = () => {
    trackUserInteraction({
      userInteractionData: {
        action: UserActionEnum.call,
        elementId: `contact-footer_call`,
      },
      listing,
      shop,
      pageType: pageTrackingTypes?.pageType,
      templateType: pageTrackingTypes?.templateType,
      language,
      countryCode,
      filters,
      isMapToggledOn,
    });
  };

  const trackItemNavigation = (navigationData: NavigationTrackingContext) =>
    trackNavigationCustomEvent({
      navigationData,
      language,
      countryCode,
      listing,
      shop,
      pageType: pageTrackingTypes?.pageType,
      templateType: pageTrackingTypes?.templateType,
      filters,
      isMapToggledOn,
      measurementSystem,
      searchMode: searchMode ?? SearchMode.CLASSIC,
    });

  const accordions: FooterAccordionProps[] | undefined = blok.links_groups?.map(({ title, details }, groupIndex) => ({
    title: title,
    defaultExpanded: false,
    details: details.map(({ label, link }, linkIndex) => ({
      ...linkResolver({ linkObject: link, label, externalLinkDomains: externalLinkDomains ?? [] }),
      as: 'a',
      onClick: () =>
        trackItemNavigation({
          elementId: `${ELEMENT_IDS.menuFooter.title}-${groupIndex + 1}_${ELEMENT_IDS.menuFooter.link}-${linkIndex + 1}`,
          href: getLinkUrl(link),
        }),
    })),
  }));

  const parsedSocialIcons = blok.social_channels_links
    .filter(({ link }) => link)
    .map(({ _uid, link, type }) => {
      return {
        id: _uid,
        label: type,
        icon: type,
        href: link,
        onClick: () => {
          trackItemNavigation({
            elementId: `contact-footer_${type}`,
            href: link,
          });
        },
      };
    });

  const parsedSettings = [
    {
      ...linkResolver({
        linkObject: blok.cookie_hints_link,
        label: blok.cookie_hints_label,
        externalLinkDomains: externalLinkDomains ?? [],
      }),
      id: blok.cookie_hints_label,
      onClick: () => {
        trackItemNavigation({
          elementId: `${ELEMENT_IDS.metaFooter.title}_${ELEMENT_IDS.metaFooter.cookiePolicy}`,
          href: getLinkUrl(blok.cookie_hints_link),
        });
      },
    },
  ].filter(({ href }) => href);

  const parsedLegals = [
    {
      ...linkResolver({
        linkObject: blok.data_privacy_link,
        label: blok.data_privacy_label,
        externalLinkDomains: externalLinkDomains ?? [],
      }),
      id: blok.data_privacy_label,
      onClick: () => {
        trackLegalLink(ELEMENT_IDS.metaFooter.privacyPolicy, blok.data_privacy_link);
      },
    },
    {
      ...linkResolver({
        linkObject: blok.imprint_link,
        label: blok.imprint_label,
        externalLinkDomains: externalLinkDomains ?? [],
      }),
      id: blok.imprint_label,
      onClick: () => {
        trackLegalLink(ELEMENT_IDS.metaFooter.imprint, blok.imprint_link);
      },
    },
  ].filter(({ href }) => href);

  const contactProps: ContactsProps = {
    headline: blok.headline,
    phone: {
      value: formatPhoneLink(blok.phone, false),
      onClick: trackPhoneClick,
    },
    location: blok.direction,
    socialChannels: {
      socialIcons: parsedSocialIcons,
    },
    toTopLabel: blok.to_top_label,
  };

  return (
    <Footer
      contacts={contactProps}
      accordions={accordions}
      settings={{
        settingLinks: parsedSettings,
        legalLinks: parsedLegals,
        cookieSettingLink: {
          label: blok.cookie_setting_label,
          onClick: () => {
            if (didomi.isReady) {
              didomi.showPreferences();
            }
            trackItemNavigation({
              elementId: `${ELEMENT_IDS.metaFooter.title}_${ELEMENT_IDS.metaFooter.cookieSettings}`,
            });
          },
          as: 'button',
        },
      }}
    />
  );
};
